import {
  DateInput,
  FormGroup,
  FormGroupFeedback,
  Icon16,
  IconNames16,
  IDateInputProps as IThumbtackDateInputProps,
  IFormGroupProps,
  Intent,
  IPopoverProps,
} from '@pinpointhq/thumbtack';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import * as React from 'react';

interface IInputProps extends Omit<IThumbtackDateInputProps['inputProps'], 'intent' | 'leftIcon'> {
  /**
   * Name of a Thumbtack icon to render before the input.
   * The icon will be rendered as an `<svg>`.
   */
  leftIcon?: IconNames16;
}

interface IDateInputProps extends Omit<IThumbtackDateInputProps, 'inputProps'> {
  inputProps: IInputProps;
}

interface IDateField {
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  dateInputProps?: IDateInputProps;
  popoverProps?: Pick<IPopoverProps, 'fill'>;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

export function DateField<T extends string>({
  dateInputProps,
  name,
  maxDate,
  minDate,
  disabled,
  formGroupProps,
  popoverProps,
}: IDateField) {
  const [field, meta, { setValue }]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  // When setting the minDate and maxDate we need to make sure that the time is set to 00:00:00 so that
  // the comparison to check if the date is in the range is correct.
  //
  // Issue that caused us to have to do this:
  // Depending on the timezone of the agent (browser) `new Date()` will return a date with the local timezone
  // Example: new Date(2022-01-01) will return 2022-01-01T00:00:00 in the UK, but 2022-01-01T01:00:00 in Poland
  // When in Poland the isDateInRange will return false when a user will pick 2022-01-01 (datepicker will set 2022-01-01T00:00:00 as a value)
  // because 2022-01-01T00:00:00 is before 2022-01-01T01:00:00
  const minDateStandardised = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0, 0)
    : undefined;
  const maxDateStandardised = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 0, 0, 0, 0)
    : undefined;

  // If the date input has a max and min date and you start typing date fns does its best to create a date out of
  // any old shit (e.g. if you type in 2 it will set the date to 1st Feb 2021). The problem is that date could be
  // outside of the date range specified so we need to set the value to be null if that's the case.
  //
  const isDateInRange = (date: Date) => {
    if (maxDateStandardised && isAfter(date, maxDateStandardised)) return false;
    if (minDateStandardised && isBefore(date, minDateStandardised)) return false;

    return true;
  };

  let value: Date;
  if (field.value) {
    value = parse(field.value);

    value = isDateInRange(value) ? value : null;
  }

  function handleChange(selectedDate: Date, isUserChange: boolean) {
    if (isUserChange) {
      if (selectedDate) {
        setValue(format(selectedDate, 'YYYY-MM-DD') as T);
      } else {
        setValue(null as T);
      }
    }
  }

  const { inputProps, ...restDateInputProps } = dateInputProps || { inputProps: undefined };
  const { leftIcon: icon, ...restInputProps } = inputProps || { leftIcon: null };
  const leftIcon = icon && <Icon16 icon={icon} />;

  /**
   * This shouldn't need the portal: false, but navigating to the page via Turbolinks will
   * cause the datepicker to not show up unless we set it. I would have thought the portalContainer
   * fix in Thumbtack would have addressed the issue, but apparently not
   */
  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <DateInput
        value={value}
        onChange={handleChange}
        maxDate={maxDateStandardised}
        minDate={minDateStandardised}
        disabled={disabled}
        inputProps={{ ...field, intent: hasError ? Intent.DANGER : Intent.NONE, ...restInputProps, leftIcon }}
        popoverProps={{ usePortal: false, ...popoverProps }}
        {...restDateInputProps}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}
